/**
 * Segment Chat API Service
 *
 * API client functions for block-level (segment) chat with AI therapist.
 * Includes streaming support via Server-Sent Events (SSE) for real-time responses.
 *
 * Key responsibilities:
 * - Create/get/delete segment chat sessions
 * - Stream AI responses token-by-token via SSE
 * - Save complete assistant messages after streaming
 * - Handle authentication and error recovery
 */

import { apiClient } from "./api";
import type {
  SaveAssistantMessageRequest,
  SegmentChatCreate,
  SegmentChatMessageCreate,
  SegmentChatMessageResponse,
  SegmentChatResponse,
  StreamChunk,
} from "@/types/segmentChat";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8101";
const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

/**
 * Get access token for authenticated requests
 */
let getAccessToken: (() => Promise<string>) | null = null;

export function setSegmentChatTokenGetter(getter: () => Promise<string>) {
  getAccessToken = getter;
}

/**
 * Create a new chat session or get existing one for a segment
 */
export async function createOrGetSegmentChat(
  transcriptId: string,
  segmentId: string,
  request?: SegmentChatCreate
): Promise<SegmentChatResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/transcripts/${transcriptId}/segments/${segmentId}/chat`,
    request || {}
  );
  return response.data;
}

/**
 * Get existing chat session for a segment
 */
export async function getSegmentChat(
  transcriptId: string,
  segmentId: string
): Promise<SegmentChatResponse> {
  const response = await apiClient.get(
    `${API_PREFIX}/transcripts/${transcriptId}/segments/${segmentId}/chat`
  );
  return response.data;
}

/**
 * Get all chat sessions for all segments in a transcript (batch endpoint)
 *
 * This is more efficient than fetching individual segment chats.
 * Returns empty array if no chats exist yet.
 */
export async function getAllSegmentChats(
  transcriptId: string
): Promise<SegmentChatResponse[]> {
  const response = await apiClient.get(
    `${API_PREFIX}/transcripts/${transcriptId}/chats`
  );
  return response.data;
}

/**
 * Stream a message and receive AI response via Server-Sent Events
 *
 * This function opens an SSE connection and yields chunks as they arrive.
 * The caller is responsible for accumulating chunks and calling saveAssistantMessage
 * when the stream completes.
 *
 * @param transcriptId - ID of the transcript
 * @param segmentId - ID of the segment
 * @param message - User message to send
 * @param onChunk - Callback invoked for each streaming chunk
 * @returns Promise that resolves when stream completes or rejects on error
 */
export async function sendMessageStreaming(
  transcriptId: string,
  segmentId: string,
  message: SegmentChatMessageCreate,
  onChunk: (chunk: StreamChunk) => void
): Promise<void> {
  // Get auth token
  let token: string | null = null;
  if (getAccessToken) {
    try {
      token = await getAccessToken();
    } catch (error) {
      throw new Error("Failed to get authentication token");
    }
  }

  // Build URL
  const url = `${API_BASE_URL}${API_PREFIX}/transcripts/${transcriptId}/segments/${segmentId}/chat/messages/stream`;

  // Send request with fetch for streaming support
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(message),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Streaming request failed: ${response.status} ${errorText}`
    );
  }

  if (!response.body) {
    throw new Error("Response body is null");
  }

  // Parse SSE stream
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let streamComplete = false;

  try {
    while (!streamComplete) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Decode and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        // SSE format: "data: {json}\n\n"
        if (line.startsWith("data: ")) {
          const data = line.slice(6); // Remove "data: " prefix

          try {
            const chunk = JSON.parse(data) as StreamChunk;
            onChunk(chunk);

            // Stop reading if error or complete
            if (chunk.type === "error" || chunk.type === "complete") {
              streamComplete = true;
              break;
            }
          } catch (error) {
            console.error("Failed to parse SSE chunk:", error, data);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Save complete assistant message after streaming completes
 *
 * The streaming endpoint doesn't persist the assistant message.
 * After accumulating all chunks, call this to save the complete message.
 */
export async function saveAssistantMessage(
  transcriptId: string,
  segmentId: string,
  request: SaveAssistantMessageRequest
): Promise<SegmentChatMessageResponse> {
  const response = await apiClient.post(
    `${API_PREFIX}/transcripts/${transcriptId}/segments/${segmentId}/chat/messages`,
    request
  );
  return response.data;
}

/**
 * Delete chat session and all messages for a segment
 */
export async function deleteSegmentChat(
  transcriptId: string,
  segmentId: string
): Promise<void> {
  await apiClient.delete(
    `${API_PREFIX}/transcripts/${transcriptId}/segments/${segmentId}/chat`
  );
}
